if (!customElements.get('slideshow-component')) {
    customElements.define(
        'slideshow-component',
        class SlideshowComponent extends HTMLElement {
            constructor() {
                super();

                this.sliderControls = this.querySelector('.swiper-controls');
                this.sliderInstance = false;
                this.enableSlider = this.dataset.enableSlider === 'true';
                this.selectedIndex = this.selectedIndex;
                this.sectionId = this.dataset.sectionId;
                this.section = this.closest(`.section-${this.sectionId}`);
                this.containerWidth = this.dataset.container;
                this.cleanupScrollAnimations = [];
                this.enableZoom = this.dataset.enableZoom
                    ? this.dataset.enableZoom === 'true'
                    : true;
                this.borderRadius = this.dataset.borderRadius;
                this.zoom = 0;

                /** ---------------------------------------------------------
                 * [state : 화면별 슬라이드 목록과 이벤트 참조]
                 *
                 * 1. 보관: Swiper가 반복용 슬라이드를 복제하기 전에 원본 목록 저장
                 * 2. 기준: slideshow.liquid의 노출 CSS와 동일하게 767px 이하를 모바일로 구분
                 * 3. 해제: 등록한 이벤트를 정확히 제거할 수 있도록 같은 함수 참조 유지
                 * ------------------------------------------------------------ */
                this.slides = Array.from(this.querySelector('.swiper-wrapper').children);
                this.visibleSlides = null;
                this.mobileQuery = window.matchMedia('(max-width: 767px)');
                this.handleViewportChange = this.initSlider.bind(this);
                this.handleResize = this.setupZoom.bind(this);
                this.handleFocusIn = this.handleFocusIn.bind(this);
            }

            /** ---------------------------------------------------------
             * [lifecycle : 슬라이더 초기화와 화면 전환 감지]
             *
             * 1. 유지: 슬라이더 사용이 꺼진 단일 블록은 기존 동작 유지
             * 2. 갱신: PC·모바일 경계가 바뀌면 현재 화면에 표시할 슬라이드 재계산
             * 3. 연결: 개별 링크 대신 컴포넌트에서 포커스 이벤트를 받아 재초기화 시 중복 등록 방지
             * ------------------------------------------------------------ */
            connectedCallback() {
                if (!this.enableSlider) return;

                this.setupZoom();
                window.addEventListener('resize', this.handleResize);
                this.mobileQuery.addEventListener('change', this.handleViewportChange);
                this.addEventListener('focusin', this.handleFocusIn);

                this.initSlider();
            }

            /** ---------------------------------------------------------
             * [lifecycle : 섹션 제거 시 이벤트와 슬라이더 정리]
             *
             * 1. 해제: 테마 편집기에서 섹션을 제거·교체할 때 이전 이벤트와 Swiper 정리
             * 2. 재연결: 같은 요소가 다시 추가되면 현재 화면 기준으로 초기화하도록 목록 상태 초기화
             * ------------------------------------------------------------ */
            disconnectedCallback() {
                window.removeEventListener('resize', this.handleResize);
                this.mobileQuery.removeEventListener('change', this.handleViewportChange);
                this.removeEventListener('focusin', this.handleFocusIn);
                this.destroySlider();
                this.visibleSlides = null;
                this.destroyZoom();
            }

            static get observedAttributes() {
                return ['selected-index'];
            }

            get selectedIndex() {
                return parseInt(this.getAttribute('selected-index')) || 0;
            }

            set selectedIndex(index) {
                this.setAttribute('selected-index', `${index}`);
            }

            // setupZoom() {
            //   if (!this.enableZoom) return;

            //   this.destroyZoom();

            //   const pageWidthEl = this.querySelector('.page-width');
            //   const paddingLeft = window.getComputedStyle(pageWidthEl).getPropertyValue('padding-left');
            //   this.zoom = Math.min(parseFloat(paddingLeft.replace('px', '')), window.innerWidth * 0.04);

            //   if (this.zoom > 15) {
            //     this.initZoom();
            //   }
            // }

            setupZoom() {
                return; // zoom 효과 비활성화
            }

            /** ---------------------------------------------------------
             * [function : 현재 화면에 표시되는 슬라이드만 활성화]
             *
             * 1. 원인: CSS로만 숨긴 PC 슬라이드가 활성 상태로 남아 모바일 슬라이드의 클릭이 차단됨
             * 2. 선택: 현재 화면의 노출 설정에 맞는 원본 슬라이드만 Swiper 동작 대상으로 지정
             * 3. 갱신: 표시할 목록이 달라질 때만 기존 슬라이더를 해제하고 새 목록으로 초기화
             * 4. 예외: 표시할 슬라이드가 없으면 생성을 생략하고, 1개면 반복·자동 재생·스와이프 해제
             * ------------------------------------------------------------ */
            initSlider() {
                const hiddenClass = this.mobileQuery.matches
                    ? 'slide--pc-only'
                    : 'slide--mobile-only';
                const visibleSlides = this.slides.filter(
                    slide => !slide.classList.contains(hiddenClass)
                );

                if (
                    this.visibleSlides &&
                    visibleSlides.length === this.visibleSlides.length &&
                    visibleSlides.every((slide, index) => slide === this.visibleSlides[index])
                ) {
                    return;
                }

                this.destroySlider();
                this.visibleSlides = visibleSlides;
                this.slides.forEach(slide => {
                    // 숨긴 블록은 편집기 선택을 위해 DOM에 남기되 Swiper의 순번 계산에서는 제외
                    slide.classList.toggle(
                        'slideshow__visible-slide',
                        visibleSlides.includes(slide)
                    );
                });

                const hasMultipleSlides = visibleSlides.length > 1;
                if (this.sliderControls) {
                    this.sliderControls.style.display = hasMultipleSlides ? '' : 'none';
                }
                if (!visibleSlides.length) {
                    this.removeAttribute('data-media-loading');
                    return;
                }

                const additionModules = [FoxTheme.Swiper.Autoplay, FoxTheme.Swiper.EffectFade];

                this.sliderOptions = {
                    slideClass: 'slideshow__visible-slide',
                    slidesPerView: 1,
                    spaceBetween: 10,
                    loop: hasMultipleSlides,
                    grabCursor: true,
                    allowTouchMove: hasMultipleSlides,
                    threshold: 2,
                    effect: 'fade',
                    on: {
                        init: this.handleAfterInit.bind(this),
                    },
                    fadeEffect: {
                        crossFade: true,
                    },
                };

                if (this.sliderControls) {
                    this.sliderOptions.navigation = {
                        nextEl: this.sliderControls.querySelector('.swiper-button-next'),
                        prevEl: this.sliderControls.querySelector('.swiper-button-prev'),
                    };
                    this.sliderOptions.pagination = {
                        el: this.sliderControls.querySelector('.swiper-pagination'),
                        clickable: true,
                    };
                }

                const autoplayDelay = parseInt(this.dataset.autoplay);
                if (autoplayDelay > 0 && hasMultipleSlides) {
                    this.sliderOptions = {
                        ...this.sliderOptions,
                        autoplay: {
                            delay: autoplayDelay,
                            disableOnInteraction: false,
                        },
                    };
                }

                this.sliderInstance = new window.FoxTheme.Carousel(
                    this,
                    this.sliderOptions,
                    additionModules
                );
                this.sliderInstance.init();

                this.sliderInstance.slider.on('realIndexChange', this.handleSlideChange.bind(this));

                if (this.sliderInstance) {
                    this.selectedElement =
                        this.sliderInstance.slider.slides[this.sliderInstance.slider.activeIndex];
                    this.selectedIndex = this.sliderInstance.slider.realIndex;
                    this.onReady(this.selectedElement, this.sliderInstance.slider.slides);
                }
            }

            /** ---------------------------------------------------------
             * [function : 화면 전환 전 재생과 Swiper 상태 정리]
             *
             * 1. 재생: 원본 슬라이드의 영상을 멈춰 숨겨진 PC 영상이 계속 재생되는 것을 방지
             * 2. 해제: Swiper 이벤트·복제 슬라이드·적용 스타일을 정리하여 재생성 시 중복 방지
             * ------------------------------------------------------------ */
            destroySlider() {
                if (!this.sliderInstance) return;

                this.slides.forEach(slide => {
                    slide.querySelectorAll('video-element').forEach(video => video.pause());
                });
                this.sliderInstance.slider.destroy(true, true);
                this.sliderInstance = false;
            }

            /** ---------------------------------------------------------
             * [event : 포커스가 들어온 슬라이드로 이동]
             *
             * 1. 대상: 기존과 같이 넘김 버튼과 btn 클래스의 버튼은 제외하고 처리
             * 2. 보호: 현재 화면의 슬라이더에 포함된 슬라이드에만 이동 처리
             * 3. 순번: 반복 모드는 원본 순번, 일반 모드는 현재 Swiper 목록의 순번으로 이동
             * ------------------------------------------------------------ */
            handleFocusIn(event) {
                const element = event.target;
                if (
                    element.classList.contains('swiper-button') ||
                    element.classList.contains('btn')
                ) {
                    return;
                }

                const slider = this.sliderInstance?.slider;
                const slide = element.closest('.slideshow__visible-slide');
                if (!slider || !slide) return;

                if (slider.params.loop) {
                    slider.slideToLoop(Number(slide.dataset.swiperSlideIndex));
                } else {
                    slider.slideTo(slider.slides.indexOf(slide));
                }
            }

            onReady(selectedElement) {
                if (selectedElement.dataset.type === 'video') {
                    const videoElement = FoxTheme.utils.displayedMedia(
                        selectedElement.querySelectorAll('video-element')
                    );
                    videoElement?.play();
                }

                if (!FoxTheme.config.motionReduced) {
                    const motionEls = selectedElement.querySelectorAll('motion-element');
                    motionEls.forEach(motionEl => {
                        setTimeout(() => {
                            motionEl && motionEl.refreshAnimation();
                        });
                    });
                }
            }

            handleAfterInit() {
                this.removeAttribute('data-media-loading');

                // Fix active bullet not transition on the first time.
                if (this.sliderControls) {
                    const activeBullet = this.sliderControls.querySelector(
                        '.swiper-pagination-bullet-active'
                    );

                    if (activeBullet) {
                        activeBullet.classList.remove('swiper-pagination-bullet-active');
                        activeBullet.offsetHeight; // Trigger reflow.
                        activeBullet.classList.add('swiper-pagination-bullet-active');
                    }
                }
            }

            handleSlideChange(swiper) {
                const { slides, realIndex, activeIndex } = swiper;
                this.selectedIndex = realIndex;

                this.updateControlsScheme(slides[activeIndex]);
            }

            attributeChangedCallback(name, oldValue, newValue) {
                if (name === 'selected-index' && oldValue !== null && oldValue !== newValue) {
                    const fromElements = this.querySelectorAll(
                        `[data-swiper-slide-index="${oldValue}"]`
                    );
                    const toElements = this.querySelectorAll(
                        `[data-swiper-slide-index="${newValue}"]`
                    );

                    fromElements.forEach(fromElement => {
                        if (fromElement.dataset.type === 'video') {
                            const videoElement = FoxTheme.utils.displayedMedia([
                                fromElement.querySelector('video-element'),
                            ]);
                            videoElement && videoElement.pause();
                        }

                        const motionEls = fromElement.querySelectorAll('motion-element');
                        motionEls &&
                            motionEls.forEach(motionEl => {
                                if (motionEl.hasAttribute('data-text')) {
                                    motionEl.resetAnimation();
                                }
                            });
                    });

                    toElements.forEach(toElement => {
                        setTimeout(() => {
                            if (toElement.classList.contains('swiper-slide-active')) {
                                if (toElement.dataset.type === 'video') {
                                    const videoElement = FoxTheme.utils.displayedMedia([
                                        toElement.querySelector('video-element'),
                                    ]);
                                    videoElement && videoElement.play();
                                }

                                const motionEls = toElement.querySelectorAll('motion-element');
                                motionEls.forEach(motionEl => {
                                    setTimeout(() => {
                                        motionEl && motionEl.refreshAnimation();
                                    });
                                });
                            }
                        });
                    });
                }
            }

            updateControlsScheme(activeSlide) {
                if (this.sliderControls) {
                    const classesToRemove = Array.from(this.sliderControls.classList).filter(
                        className => className.startsWith('color-')
                    );
                    classesToRemove.forEach(className =>
                        this.sliderControls.classList.remove(className)
                    );
                    const colorScheme = activeSlide.dataset.colorScheme;
                    this.sliderControls.classList.add(colorScheme);
                }
            }

            initZoom() {
                if (this.containerWidth !== 'full') return;

                const motionTarget = {
                    target: this.section,
                    offset: [
                        [-0.1, 0],
                        [0.7, 0],
                    ],
                };

                this.querySelectorAll('.slideshow__item-wrap').forEach(slide => {
                    const slideBg = slide.querySelector('.slideshow__bg');

                    this.cleanupScrollAnimations.push(
                        FoxTheme.Motion.scroll(
                            FoxTheme.Motion.animate(slideBg, {
                                transform: [`scale(1) translateZ(0)`, `scale(1.1) translateZ(0)`],
                            }),
                            motionTarget
                        )
                    );
                });

                this.cleanupScrollAnimations.push(
                    FoxTheme.Motion.scroll(
                        FoxTheme.Motion.animate(this, {
                            clipPath: [
                                `inset(0%)`,
                                `inset(${Math.min(50, this.zoom)}px ${this.zoom}px round ${this.borderRadius}px)`,
                            ],
                        }),
                        motionTarget
                    )
                );

                if (this.sliderControls) {
                    this.cleanupScrollAnimations.push(
                        FoxTheme.Motion.scroll(
                            FoxTheme.Motion.animate(this.sliderControls, {
                                bottom: [`0`, `${Math.min(50, this.zoom)}px`],
                            }),
                            motionTarget
                        )
                    );
                }
            }

            destroyZoom() {
                this.cleanupScrollAnimations.forEach(fn => fn());
                this.cleanupScrollAnimations = [];
                this.style.clipPath = 'none';
                if (this.sliderControls) this.sliderControls.style.bottom = 0;
            }
        }
    );
}
